import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { isNativeApp } from "@/lib/nativeAuthStorage";
import NotificationPreferencesDialog from "@/components/NotificationPreferencesDialog";
import TaskConversationAlerts from "@/components/tasks/TaskConversationAlerts";
import TaskCompletionAlerts from "@/components/tasks/TaskCompletionAlerts";
import useSidebarBadges from "@/hooks/useSidebarBadges";
import useBrowserTabAlert from "@/hooks/useBrowserTabAlert";
import { filterSidebarBadges } from "@/lib/sidebarBadges";
import { preloadRoute } from "@/lib/routePreload";
import { getRoleLabel, isReadOnlyReviewer } from "@/lib/roles";
import { LayoutDashboard, FolderOpen, Users, LogOut, Menu, MoreVertical, ChevronRight, Moon, Sun, FileText, ClipboardList, BarChart3, Building2, Calendar, KeyRound, HardHat, BellRing, DollarSign, ShieldCheck } from "lucide-react";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/", roles: ["admin", "project_manager", "field_lead", "technician", "accounting"] },
  { label: "Tasks", icon: ClipboardList, path: "/tasks", roles: ["admin", "project_manager", "field_lead", "technician", "accounting"], badgeKey: "tasks" },
  { label: "Projects", icon: FolderOpen, path: "/projects", roles: ["admin", "project_manager", "field_lead", "technician", "accounting"] },
  { label: "Reports", icon: FileText, path: "/reports", roles: ["admin", "project_manager"], badgeKey: "reports" },
  { label: "Project Status Report", icon: ClipboardList, path: "/project-status-report", roles: ["admin", "project_manager", "accounting"] },
  { label: "Accounting Alerts", icon: BellRing, path: "/accounting-alerts", roles: ["admin", "accounting"], badgeKey: "accountingAlerts" },
  { label: "Invoice Review", icon: DollarSign, path: "/accounting", roles: ["admin", "accounting"], badgeKey: "accounting" },
  { label: "Deadlines", icon: Calendar, path: "/deadlines", roles: ["admin", "project_manager", "field_lead", "technician", "accounting"], badgeKey: "deadlines" },
  { label: "Calendar", icon: Calendar, path: "/calendar", roles: ["admin", "project_manager", "field_lead", "technician", "accounting"] },
  { label: "Assignment Tracker", icon: BarChart3, path: "/tracker", roles: ["admin", "project_manager"] },
  { label: "Clients", icon: Building2, path: "/clients", roles: ["admin", "project_manager"] },
  { label: "Sub-Contractors", icon: HardHat, path: "/sub-contractors", roles: ["admin", "project_manager"] },
  { label: "Team", icon: Users, path: "/users", roles: ["admin", "project_manager"] },
];

function SidebarContent({ user, pathname, onNavigate, badges }) {
  return (
    <div className="flex h-full w-full flex-col bg-[#1F3127] text-white">
      <div className="p-4">
        <div className="inline-flex bg-white rounded px-3 py-2 shadow-sm">
          <img
            src="/brand/whitenton-logo.png"
            alt="Whitenton Group Environmental Consultants"
            className="h-11 w-auto"
            data-testid="sidebar-logo"
          />
        </div>
      </div>
      <Separator className="bg-white/10" />
      <nav aria-label="Main navigation" className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
        {navItems
          .filter((item) => item.roles.includes(user?.role) || (isReadOnlyReviewer(user) && !["/users", "/calendar"].includes(item.path)))
          .map((item) => {
            const isActive = item.path === "/"
              ? pathname === "/"
              : pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                onFocus={() => preloadRoute(item.path)}
                onMouseEnter={() => preloadRoute(item.path)}
                onTouchStart={() => preloadRoute(item.path)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-white/15 text-white"
                    : "text-white/60 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1">{item.label}</span>
                {item.badgeKey && badges?.[item.badgeKey] > 0 && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#B85C42] text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid={`nav-badge-${item.badgeKey}`}
                    aria-label={`${badges[item.badgeKey]} ${item.label.toLowerCase()} need attention`}
                  >
                    {badges[item.badgeKey] > 99 ? "99+" : badges[item.badgeKey]}
                  </span>
                )}
                {isActive && <ChevronRight className="h-4 w-4" />}
              </Link>
            );
          })}
      </nav>
      <Separator className="bg-white/10" />
      <div className="p-4">
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar className="h-8 w-8 border border-white/20">
            <AvatarFallback className="bg-[#284A36] text-white text-xs">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/50">{getRoleLabel(user?.role)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname, key: locationKey } = useLocation();
  const nativeApp = isNativeApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [notifPrefsDialog, setNotifPrefsDialog] = useState(false);
  const readOnly = isReadOnlyReviewer(user);
  const { conversations, completionNotifications, ...badges } = useSidebarBadges();
  const [dashboardBadgePreferences, setDashboardBadgePreferences] = useState({});
  const [badgePreferencesLoaded, setBadgePreferencesLoaded] = useState(false);
  const sidebarBadges = useMemo(
    () => filterSidebarBadges(badges, dashboardBadgePreferences),
    [badges, dashboardBadgePreferences],
  );
  useBrowserTabAlert(sidebarBadges);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") return document.documentElement.classList.contains("dark");
    return false;
  });

  useEffect(() => {
    setMobileOpen(false);
  }, [locationKey]);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/notification-preferences")
      .then((response) => {
        if (!cancelled) {
          setDashboardBadgePreferences(response.data?.dashboard_badges || {});
          setBadgePreferencesLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setBadgePreferencesLoaded(true); });
    const onChanged = (event) => {
      setDashboardBadgePreferences(event.detail || {});
      setBadgePreferencesLoaded(true);
    };
    window.addEventListener("whitenton:sidebar-badges-changed", onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("whitenton:sidebar-badges-changed", onChanged);
    };
  }, []);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") { document.documentElement.classList.add("dark"); setDark(true); }
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed successfully");
      setPasswordDialog(false);
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
  };

  const pageTitle = navItems.find(
    (item) => item.path === "/" ? pathname === "/" : pathname.startsWith(item.path)
  )?.label || "Dashboard";

  return (
    <div className="flex h-screen bg-[#F7F8F6] dark:bg-[#121212]" data-testid="main-layout">
      {!nativeApp && (
        <aside className="hidden md:flex w-64 flex-shrink-0">
          <SidebarContent user={user} pathname={pathname} badges={sidebarBadges} />
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-[#161616]/80 backdrop-blur-xl border-b border-[#E5E7E2] dark:border-white/10 px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={`h-9 w-9 shrink-0 ${nativeApp ? "" : "md:hidden"}`}
                  aria-label="Open navigation menu"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 max-w-[85vw] p-0 [&>button]:text-white" aria-describedby={undefined}>
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SidebarContent user={user} pathname={pathname} onNavigate={() => setMobileOpen(false)} badges={sidebarBadges} />
              </SheetContent>
            </Sheet>
            <h1 className="text-base sm:text-lg font-semibold text-[#16201A] font-heading truncate">{pageTitle}</h1>
            {readOnly && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded border border-[#CBA153]/40 bg-[#CBA153]/10 px-2 py-1 text-xs font-semibold text-[#7A5B20]">
                <ShieldCheck className="h-3.5 w-3.5" /> Read-only review
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            {!readOnly && <TaskCompletionAlerts key={`completion-${user?.id}`} notifications={completionNotifications} alertsEnabled={badgePreferencesLoaded && dashboardBadgePreferences.tasks !== false} />}
            {!readOnly && <TaskConversationAlerts key={user?.id} conversations={conversations} userId={user?.id}
              alertsEnabled={badgePreferencesLoaded && dashboardBadgePreferences.tasks !== false} />}
            {!readOnly && <Button
              variant="ghost"
              size="icon"
              onClick={() => setNotifPrefsDialog(true)}
              className="h-9 w-9 sm:h-10 sm:w-10 text-[#647069] hover:text-[#16201A] dark:text-white/60 dark:hover:text-white hover:bg-[#F7F8F6] dark:hover:bg-white/10"
              data-testid="notification-preferences-button"
              title="Notification Preferences"
            >
              <BellRing className="h-5 w-5" />
            </Button>}
            {!readOnly && <Button
              variant="ghost"
              size="icon"
              onClick={() => setPasswordDialog(true)}
              className="hidden h-9 w-9 sm:inline-flex sm:h-10 sm:w-10 text-[#647069] hover:text-[#16201A] dark:text-white/60 dark:hover:text-white hover:bg-[#F7F8F6] dark:hover:bg-white/10"
              data-testid="change-password-button"
            >
              <KeyRound className="h-5 w-5" />
            </Button>}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDark}
              className="hidden h-9 w-9 sm:inline-flex sm:h-10 sm:w-10 text-[#647069] hover:text-[#16201A] dark:text-white/60 dark:hover:text-white hover:bg-[#F7F8F6] dark:hover:bg-white/10"
              data-testid="dark-mode-toggle"
            >
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="hidden h-9 w-9 sm:inline-flex sm:h-10 sm:w-10 text-[#647069] hover:text-[#16201A] hover:bg-[#F7F8F6]"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-[#647069] sm:hidden" title="Account options" aria-label="Account options" data-testid="mobile-account-menu">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!readOnly && <DropdownMenuItem onSelect={() => setPasswordDialog(true)}><KeyRound className="mr-2 h-4 w-4" />Change password</DropdownMenuItem>}
                <DropdownMenuItem onSelect={toggleDark}>{dark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}{dark ? "Light mode" : "Dark mode"}</DropdownMenuItem>
                <DropdownMenuItem onSelect={handleLogout}><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-3 sm:p-6 md:p-8">
          {readOnly && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-[#CBA153]/35 bg-[#FFF9EC] px-3 py-2 text-sm text-[#6D5422] dark:bg-[#CBA153]/10 dark:text-[#E7CD98]" role="status">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This review account can explore the app, but cannot add, edit, delete, assign, upload, or submit data.</span>
            </div>
          )}
          {children}
        </main>
      </div>

      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="w-[calc(100vw-1.5rem)] bg-white dark:bg-[#1e1e1e]" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Change Password</DialogTitle></DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2"><Label>Current Password</Label><Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required className="border-[#E5E7E2] dark:border-white/10" data-testid="current-password-input" /></div>
            <div className="space-y-2"><Label>New Password</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} className="border-[#E5E7E2] dark:border-white/10" placeholder="Min 6 characters" data-testid="new-password-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#284A36] hover:bg-[#1E3A28] text-white" data-testid="submit-change-password">Change Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <NotificationPreferencesDialog
        open={notifPrefsDialog}
        onOpenChange={setNotifPrefsDialog}
        userEmail={user?.email}
        userRole={user?.role}
      />
    </div>
  );
}
